import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Link as MLink,
  Stack,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import LoadingOverlay from "../../components/shared/Backdrop";
import { MESSAGE } from "../../utils/validationMessage";
import errorHandler from "../../utils/errorHandler";
import { adminAuthServices } from "../../services/authService";
import { login } from "../../features/AuthSlice";
import palette from "../../theme/palette";
import { getFirstAvailableRoute } from "../../utils/helpers";

// ----------------------------------------------------------------------

const validationSchema = Yup.object().shape({
  otp: Yup.string()
    .required(MESSAGE.REQUIRED)
    .min(4, "OTP must be 4 digits")
    .max(4, "OTP must be 4 digits"),
});

export default function LoginOtpVerifyPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", ""]);

  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const { state } = useLocation();

  useEffect(() => {
    if (!state?.email) {
      toast.error("Please login first");
      navigate("/login");
    }
  }, [navigate, state?.email]);

  const formik = useFormik({
    initialValues: { otp: "" },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      adminAuthServices
        .verifyLoginOtp({ email: state?.email, otp: values.otp })
        .then((res) => {
          if (res?.data?.status) {
            toast.success(res?.data?.message);

            const { token, admin } = res.data.data;
            dispatch(login({ token, admin }));
            localStorage.setItem("token", token);
            localStorage.setItem("user", JSON.stringify(admin));

            // Get first available route based on permissions
            const redirectRoute = getFirstAvailableRoute(admin);
            navigate(redirectRoute, { replace: true });
          }
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, setFieldValue } = formik;

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^[0-9]$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    // Update formik field
    const otpString = newOtpValues.join("");
    setFieldValue("otp", otpString);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtpValues = [...otpValues];
    for (let i = 0; i < pastedData.length && i < 4; i++) {
      newOtpValues[i] = pastedData[i];
    }
    setOtpValues(newOtpValues);
    setFieldValue("otp", newOtpValues.join(""));

    // Focus the next empty input or the last one
    const nextEmptyIndex = newOtpValues.findIndex((val) => !val);
    if (nextEmptyIndex !== -1) {
      inputRefs[nextEmptyIndex].current?.focus();
    } else {
      inputRefs[3].current?.focus();
    }
  };

  const resendOtp = () => {
    setIsLoading(true);
    adminAuthServices
      .login({ email: state?.email, password: state?.password })
      .then((res) => {
        setIsLoading(false);
        toast.success("OTP resent successfully");
      })
      .catch((err) => {
        setIsLoading(false);
        errorHandler(err);
      });
  };

  return (
    <>
      <LoadingOverlay isLoading={isLoading} />
      <FormikProvider value={formik}>
        <Typography variant="h3" sx={{ mb: 2 }} align="center">
          Verify OTP
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 3, color: "text.secondary" }}
          align="center"
        >
          Enter the OTP sent to your email
        </Typography>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
              }}
            >
              {[0, 1, 2, 3].map((index) => (
                <TextField
                  key={index}
                  inputRef={inputRefs[index]}
                  value={otpValues[index]}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                    style: {
                      textAlign: "center",
                      fontSize: "24px",
                      fontWeight: "bold",
                    },
                  }}
                  sx={{
                    width: 60,
                    "& .MuiOutlinedInput-root": {
                      height: 60,
                    },
                  }}
                />
              ))}
            </Box>
            {touched.otp && errors.otp && (
              <Typography color="error" variant="caption" align="center">
                {errors.otp}
              </Typography>
            )}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ my: 2 }}
          >
            <MLink
              variant="subtitle2"
              underline="hover"
              onClick={resendOtp}
              color={palette.primary.main}
              sx={{ cursor: "pointer" }}
            >
              Resend OTP?
            </MLink>
          </Stack>
          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={otpValues.join("").length !== 4}
          >
            Verify & Login
          </LoadingButton>
        </Form>
      </FormikProvider>
    </>
  );
}
